import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import useReconnectingWebSocket from "../../../../../hooks/useReconnectingWebSocket";
import styles from "./network_section.module.css";
import NetworkQualityCard from "./network_quality_card";
import CurrentConnectionCard from "./current_connection_card";

const TABS = [
  { id: "ethernet", label: "Ethernet" },
  { id: "wifi", label: "WiFi" },
  { id: "lte", label: "LTE" },
];

function StatusCard({ data }) {
  if (!data) return <div className={styles.statusEmpty}>No conectado</div>;
  return (
    <div className={styles.statusCard}>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Estado</span>
        <span className={styles.statusValue}>{data.state || "-"}</span>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>DHCP</span>
        <span className={`${styles.statusValue} ${data.dhcp ? styles.dhcpOn : styles.dhcpOff}`}>
          {data.dhcp === true ? "Activado" : data.dhcp === false ? "Desactivado (manual)" : "-"}
        </span>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>IP</span>
        <span className={styles.statusValue}>{data.ip || "-"}</span>
      </div>
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Gateway</span>
        <span className={styles.statusValue}>{data.gateway || "-"}</span>
      </div>
      {data.dns && (
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>DNS</span>
          <span className={styles.statusValue}>{Array.isArray(data.dns) ? data.dns.join(", ") : data.dns}</span>
        </div>
      )}
      {data.ssid && (
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>SSID</span>
          <span className={styles.statusValue}>{data.ssid}</span>
        </div>
      )}
    </div>
  );
}

export default function NetworkSection({ pdui_id }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("ethernet");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [networkData, setNetworkData] = useState(null);
  const [wifiNetworks, setWifiNetworks] = useState([]);
  const [lteInfo, setLteInfo] = useState(null);
  const [scanning, setScanning] = useState(false);
  // Calidad de red: muestras que llegan en vivo (network_sample) y la
  // confirmación del equipo al cambiar el muestreo (network_probe_config_status).
  const [netSamples, setNetSamples] = useState([]);
  const [probeConfigStatus, setProbeConfigStatus] = useState(null);
  // Conexión actual (network_state) y confirmación del orden de medios
  // (network_priority_status) que manda el equipo.
  const [liveNetState, setLiveNetState] = useState(null);
  const [livePriorityStatus, setLivePriorityStatus] = useState(null);
  // Medios que el equipo no tiene (p. ej. sin WiFi): sin pestaña ni estado.
  const [absentMedia, setAbsentMedia] = useState([]);
  const tabsRef = useRef(null);
  const tabs = TABS.filter((tab) => !absentMedia.includes(tab.id));
  const shownTab = absentMedia.includes(activeTab) ? "ethernet" : activeTab;

  useEffect(() => {
    if (absentMedia.includes(activeTab)) setActiveTab("ethernet");
  }, [absentMedia, activeTab]);

  // "Configurar" en la tarjeta Conexión actual: abre la pestaña de ese medio.
  const openConfig = (medium) => {
    setActiveTab(medium);
    setTimeout(() => tabsRef.current && tabsRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const [ethForm, setEthForm] = useState({
    dhcp: true,
    ip: "",
    subnet: "255.255.255.0",
    gateway: "",
    dns: "8.8.8.8",
  });

  const [wifiForm, setWifiForm] = useState({
    dhcp: true,
    ssid: "",
    password: "",
    ip: "",
    subnet: "255.255.255.0",
    gateway: "",
    dns: "8.8.8.8",
  });

  const [lteForm, setLteForm] = useState({
    apn: "",
    user: "",
    password: "",
    pin: "",
  });

  // Recibe network_status, wifi_scan y lte_status reenviados desde el
  // equipo. Reconecta solo si se cae.
  const wsConnected = useReconnectingWebSocket({
    path: `/ws/client/pdui_${pdui_id}_network/`,
    pduiId: Number(pdui_id),
    label: "WebSocket de red",
    onMessage: (data) => {
      if (data.type !== "remote_mgmt_message") return;
      if (data.topic === "network_status") {
        const payload = data.payload || {};
        if (payload.config_applied !== undefined) {
          // Resultado de aplicar una configuración (no es el estado de red).
          const fallback = payload.config_applied ? t('network.resultApplied')
            : t(payload.rolled_back ? 'network.resultRolledBack' : 'network.resultFailed');
          setMessage({
            type: payload.config_applied ? (payload.saved_only ? "warning" : "success") : "error",
            text: t('network.deviceSays', { message: payload.message || fallback }),
          });
          setLoading(false);
          return;
        }
        setNetworkData(payload);
        setMessage({ type: "success", text: t('network.statusReceived') });
        setLoading(false);
      } else if (data.topic === "wifi_scan") {
        setWifiNetworks(data.payload?.networks || []);
        setScanning(false);
        setMessage({ type: "success", text: `${(data.payload?.networks || []).length} redes WiFi encontradas` });
      } else if (data.topic === "lte_status") {
        setLteInfo(data.payload);
        setScanning(false);
        setMessage({ type: "success", text: "Estado LTE recibido" });
      } else if (data.topic === "network_sample") {
        const sample = { ...(data.payload || {}), _receivedAt: Date.now() };
        setNetSamples((prev) => [...prev.slice(-30), sample]);
      } else if (data.topic === "network_probe_config_status") {
        setProbeConfigStatus(data.payload || null);
      } else if (data.topic === "network_state") {
        setLiveNetState(data.payload || null);
      } else if (data.topic === "network_priority_status") {
        setLivePriorityStatus({ ...(data.payload || {}), _receivedAt: Date.now() });
      }
    },
  });

  // Pre-fill forms when network data arrives
  useEffect(() => {
    if (!networkData) return;

    if (networkData.ethernet) {
      const e = networkData.ethernet;
      const ip = (e.configured_ip || e.ip || "").replace(/\/\d+$/, "");
      setEthForm((prev) => ({
        ...prev,
        dhcp: e.dhcp !== undefined ? e.dhcp : prev.dhcp,
        ip: ip || prev.ip,
        gateway: e.configured_gateway || e.gateway || prev.gateway,
        dns: e.configured_dns || (Array.isArray(e.dns) ? e.dns[0] : e.dns) || prev.dns,
      }));
    }

    if (networkData.wifi) {
      const w = networkData.wifi;
      const ip = (w.configured_ip || w.ip || "").replace(/\/\d+$/, "");
      setWifiForm((prev) => ({
        ...prev,
        dhcp: w.dhcp !== undefined ? w.dhcp : prev.dhcp,
        ssid: w.ssid || prev.ssid,
        ip: ip || prev.ip,
        gateway: w.configured_gateway || w.gateway || prev.gateway,
        dns: w.configured_dns || (Array.isArray(w.dns) ? w.dns[0] : w.dns) || prev.dns,
      }));
    }

    if (networkData.lte) {
      const l = networkData.lte;
      setLteForm((prev) => ({
        ...prev,
        apn: l.apn || prev.apn,
        user: l.user || prev.user,
      }));
    }
  }, [networkData]);

  const scanWifi = async () => {
    setScanning(true);
    setMessage({ type: "info", text: t('network.scanningWifi') });
    try {
      await axiosInstance.post(`pdui/${pdui_id}/wifi-scan/`);
      setTimeout(() => setScanning((p) => { if (p) setMessage({ type: "warning", text: t('network.statusTimeout') }); return false; }), 30000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Error" });
      setScanning(false);
    }
  };

  const scanLte = async () => {
    setScanning(true);
    setMessage({ type: "info", text: t('network.scanningLte') });
    try {
      await axiosInstance.post(`pdui/${pdui_id}/lte-status/`);
      setTimeout(() => setScanning((p) => { if (p) setMessage({ type: "warning", text: t('network.statusTimeout') }); return false; }), 30000);
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || "Error" });
      setScanning(false);
    }
  };

  const submitConfig = async (type, data) => {
    setLoading(true);
    setMessage(null);
    try {
      await axiosInstance.put(`pdui/${pdui_id}/remote-network/`, {
        type,
        ...data,
      });
      setMessage({
        type: "success",
        text: t('network.configSent', { type }),
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.error || t('network.configError'),
      });
    } finally {
      setLoading(false);
    }
  };

  const requestStatus = async () => {
    setLoading(true);
    setMessage(null);
    try {
      await axiosInstance.get(`pdui/${pdui_id}/remote-network/`);
      setMessage({
        type: "info",
        text: t('network.statusRequest'),
      });
      setTimeout(() => {
        setLoading((prev) => {
          if (prev) setMessage({ type: "warning", text: t('network.statusTimeout') });
          return false;
        });
      }, 30000);
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.error || t('network.statusError'),
      });
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>{t('network.title')}</div>
        <button
          className={styles.button}
          onClick={requestStatus}
          disabled={loading}
        >
          {loading ? t('network.requesting') : t('network.viewStatus')}
        </button>
      </div>

      <CurrentConnectionCard pdui_id={pdui_id} liveState={liveNetState} livePriorityStatus={livePriorityStatus}
                             onConfigure={openConfig} onAbsentMedia={setAbsentMedia} />

      <NetworkQualityCard
        pdui_id={pdui_id}
        liveSamples={netSamples}
        configStatus={probeConfigStatus}
        absentMedia={absentMedia}
      />

      <div className={styles.warning}>
        {t('network.warningLine1')}
        {' '}{t('network.warningLine2')}
      </div>

      {message && (
        <div className={`${styles.alert} ${styles["alert" + message.type.charAt(0).toUpperCase() + message.type.slice(1)]}`}>
          {message.text}
        </div>
      )}

      {networkData && (
        <div className={styles.statusGrid}>
          {tabs.map((tab) => (
            <div key={tab.id}>
              <div className={styles.statusTitle}>{tab.label}</div>
              <StatusCard data={networkData[tab.id]} />
            </div>
          ))}
        </div>
      )}

      <div className={styles.tabs} ref={tabsRef}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${shownTab === tab.id ? styles.active : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {shownTab === "ethernet" && (
        <div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={ethForm.dhcp}
                onChange={(e) => setEthForm({ ...ethForm, dhcp: e.target.checked })}
              />
              {t('network.useDhcp')}
            </label>
          </div>
          {!ethForm.dhcp && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.ipAddress')}</label>
                <input
                  className={styles.input}
                  value={ethForm.ip}
                  onChange={(e) => setEthForm({ ...ethForm, ip: e.target.value })}
                  placeholder="192.168.1.80"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.subnet')}</label>
                <input
                  className={styles.input}
                  value={ethForm.subnet}
                  onChange={(e) => setEthForm({ ...ethForm, subnet: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.gateway')}</label>
                <input
                  className={styles.input}
                  value={ethForm.gateway}
                  onChange={(e) => setEthForm({ ...ethForm, gateway: e.target.value })}
                  placeholder="192.168.1.1"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.dns')}</label>
                <input
                  className={styles.input}
                  value={ethForm.dns}
                  onChange={(e) => setEthForm({ ...ethForm, dns: e.target.value })}
                />
              </div>
            </>
          )}
          <button
            className={`${styles.button} ${styles.buttonOrange}`}
            onClick={() => submitConfig("ethernet", ethForm)}
            disabled={loading}
          >
            {t('network.sendEthernet')}
          </button>
        </div>
      )}

      {shownTab === "wifi" && (
        <div>
          <button
            className={styles.button}
            onClick={scanWifi}
            disabled={scanning}
            style={{ marginBottom: 16 }}
          >
            {scanning ? t('network.scanningWifi') : t('network.scanWifi')}
          </button>

          {wifiNetworks.length > 0 && (
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('network.availableNetworks')}</label>
              <div className={styles.networkList}>
                {wifiNetworks.map((net) => (
                  <div
                    key={net.ssid}
                    className={`${styles.networkItem} ${wifiForm.ssid === net.ssid ? styles.networkSelected : ""}`}
                    onClick={() => setWifiForm({ ...wifiForm, ssid: net.ssid })}
                  >
                    <span className={styles.networkSsid}>{net.ssid}</span>
                    <span className={styles.networkMeta}>
                      {net.signal}% - {net.security}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.ssid')}</label>
            <input
              className={styles.input}
              value={wifiForm.ssid}
              onChange={(e) => setWifiForm({ ...wifiForm, ssid: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.password')}</label>
            <input
              type="password"
              className={styles.input}
              value={wifiForm.password}
              onChange={(e) => setWifiForm({ ...wifiForm, password: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={wifiForm.dhcp}
                onChange={(e) => setWifiForm({ ...wifiForm, dhcp: e.target.checked })}
              />
              {t('network.useDhcp')}
            </label>
          </div>
          {!wifiForm.dhcp && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.ipAddress')}</label>
                <input
                  className={styles.input}
                  value={wifiForm.ip}
                  onChange={(e) => setWifiForm({ ...wifiForm, ip: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('network.gateway')}</label>
                <input
                  className={styles.input}
                  value={wifiForm.gateway}
                  onChange={(e) => setWifiForm({ ...wifiForm, gateway: e.target.value })}
                />
              </div>
            </>
          )}
          <div className={styles.warning} style={{ marginBottom: 12 }}>{t('network.wifiDualNote')}</div>
          <button
            className={`${styles.button} ${styles.buttonOrange}`}
            onClick={() => submitConfig("wifi", wifiForm)}
            disabled={loading || !wifiForm.ssid || !wifiForm.password}
          >
            {t('network.sendWifi')}
          </button>
        </div>
      )}

      {shownTab === "lte" && (
        <div>
          <button
            className={styles.button}
            onClick={scanLte}
            disabled={scanning}
            style={{ marginBottom: 16 }}
          >
            {scanning ? t('network.scanningLte') : t('network.scanLte')}
          </button>

          {lteInfo && (
            <div className={styles.statusCard} style={{ marginBottom: 16 }}>
              {lteInfo.model && (
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Modem</span>
                  <span className={styles.statusValue}>{lteInfo.model}</span>
                </div>
              )}
              <div className={styles.statusRow}>
                <span className={styles.statusLabel}>Estado</span>
                <span className={styles.statusValue}>{lteInfo.state || "-"}</span>
              </div>
              {lteInfo.operator && (
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Operador</span>
                  <span className={styles.statusValue}>{lteInfo.operator}</span>
                </div>
              )}
              {lteInfo.signal && (
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Signal</span>
                  <span className={styles.statusValue}>{lteInfo.signal}</span>
                </div>
              )}
              {lteInfo.access_tech && (
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>Tecnologia</span>
                  <span className={styles.statusValue}>{lteInfo.access_tech}</span>
                </div>
              )}
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.apn')}</label>
            <input
              className={styles.input}
              value={lteForm.apn}
              onChange={(e) => setLteForm({ ...lteForm, apn: e.target.value })}
              placeholder="claro.pe"
            />
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>{t('network.lteAutoHint')}</div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.user')}</label>
            <input
              className={styles.input}
              value={lteForm.user}
              onChange={(e) => setLteForm({ ...lteForm, user: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.password')}</label>
            <input
              type="password"
              className={styles.input}
              value={lteForm.password}
              onChange={(e) => setLteForm({ ...lteForm, password: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('network.pinSim')}</label>
            <input
              className={styles.input}
              value={lteForm.pin}
              onChange={(e) => setLteForm({ ...lteForm, pin: e.target.value })}
            />
          </div>
          <button
            className={`${styles.button} ${styles.buttonOrange}`}
            onClick={() => submitConfig("lte", lteForm)}
            disabled={loading}
          >
            {(lteForm.apn || "").trim() ? t('network.sendLte') : t('network.sendLteAuto')}
          </button>
        </div>
      )}
    </div>
  );
}
